import { useMemo } from 'react';
import { SearchInput } from '../shared/SearchInput';
import { OccupationGroupCombobox } from '../shared/OccupationGroupCombobox';
import { MetroAreaCombobox } from '../shared/MetroAreaCombobox';
import { OCCUPATIONAL_GROUPS } from '../../lib/occupational-groups';
import { useIsStuck } from '../../lib/use-is-stuck';
import { STICKY_OFFSET_BELOW_SECTION_NAV } from '../../lib/layout-offsets';
import { cn } from '../../lib/utils';
import { TableActions } from '../shared/TableActions';
import { RatioMethodologyNote } from '../rankings/RatioMethodologyNote';

interface MetroOption {
  readonly value: string;
  readonly label: string;
}

interface ProvidersFilterBarProps {
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly occupationalGroupId: string;
  readonly onOccupationalGroupChange: (value: string) => void;
  readonly metroAreaId: string;
  readonly onMetroAreaChange: (value: string) => void;
  /** Metro options populated by the parent from `useMetroData()`.
   *  Kept as `{value,label}` so the parent's URL-seed +
   *  GapCoverageCard lookup paths don't have to change shape. */
  readonly metroOptions: ReadonlyArray<MetroOption>;
  /**
   * CSV serializer for the Copy / Export buttons in the embedded
   * <TableActions /> toolbar. Built by the page (ProvidersTable)
   * from the currently-visible filtered rows so the export mirrors
   * exactly what's on screen. Optional so the bar still renders in
   * isolation (e.g. for unit tests); when omitted, the existing
   * "Nothing to copy" warn-toast fires on click. Wired 2026-06-09
   * (Slack Luis + Maria) to fix the buttons that previously appeared
   * broken because no serializer was being passed.
   */
  readonly getCsvData?: () => string;
  /**
   * Per-OG provider counts under the current filter set. Pass-through
   * to the OG combobox -- each option renders a right-aligned
   * CountBadge so users see at a glance which OGs have providers
   * under the active metro + search filters (2026-06-05 expansion of
   * IMP-017, Maria). Page sources this from
   * `useProvidersOgCounts(metroAreaId, search)`.
   *
   * `null` (or omitted) renders the combobox without badges. The
   * filter still works; the badges are a discoverability hint.
   */
  readonly ogCounts?: Readonly<Record<string, number>> | null;
  /**
   * Per-metro provider counts under the current filter set
   * (2026-06-05, Maria). Pass-through to the Metro combobox --
   * mirrors `ogCounts` for the other dropdown. Page sources this
   * from `useProvidersMetroCounts(groupIdForApi, search)`. Same
   * null/undefined semantics as `ogCounts`.
   */
  readonly metroCounts?: Readonly<Record<string, number>> | null;
}

/**
 * Sticky filter bar for the Providers page. Sits in the natural page
 * flow below the heading; pins to the top (below the Topbar +
 * SectionNav at var(--offset-nav-stack) [134px desktop] -- a calc()
 * built on the responsive var(--h-topbar); below Topbar only, at
 * var(--h-topbar) resolving to [68px, CR-028/029] on mobile <600px
 * where SectionNav is hidden -- see BUG-002 in PR #73 /
 * RankingsFilterBar JSDoc for the full root cause) once the user
 * scrolls past it. Elevation shadow lights up only when stuck.
 *
 * Filter set (2026-06-05 reorder + equal-width pass, Maria):
 *   1. Metro Area         — typeahead combobox (`MetroAreaCombobox`)
 *   2. Occupational Group — typeahead combobox (single-select, chip
 *                           slug → name resolved by parent)
 *   3. Search             — institution name, case-insensitive substring
 *   4. Copy / Export / Print — right-aligned via TableActions's
 *      default `md:ml-auto`, matching Rankings + Detail.
 *
 * All three filter slots share the same `md:w-filter-slot` width so the
 * bar reads as a row of siblings rather than a wide Metro slot +
 * narrower others. The previous `min-w-[320px] flex-1` treatment on
 * Metro (carry-over from IMP-007) made it visually dominate and was
 * dropped in favor of order + position carrying the hierarchy.
 *
 * Order history:
 *   - V1 shell: Search + OG + Metro + Criteria
 *   - DEC-057: Criteria dropped (no source data).
 *   - PR B (combobox swap, 2026-06-01): plain selects -> comboboxes.
 *   - IMP-007 (Mayuresh post-PR-72): Metro promoted to leftmost.
 *   - 2026-06-05 (Maria): Metro → OG → Search; equal widths; actions
 *     right-aligned via TableActions's default `md:ml-auto`.
 *
 * The "Criteria" placeholder dropdown from the V1 shell has been
 * REMOVED. It mapped to no source data (DEC-057 diagnostic) and was
 * a placeholder for a future feature that doesn't have a concrete
 * scope. Adding it back is a V2 conversation -- re-introduce once
 * Georgetown defines what "Criteria" actually filters on.
 *
 * Background spans the full viewport; inner controls align to the
 * 1200px page grid so they sit directly above the table columns.
 *
 * Each filter wrapper carries a `data-testid="providers-filter-slot-*"`
 * so tests can assert visual order without scraping class strings.
 */
export function ProvidersFilterBar({
  search,
  onSearchChange,
  occupationalGroupId,
  onOccupationalGroupChange,
  metroAreaId,
  onMetroAreaChange,
  metroOptions,
  getCsvData,
  ogCounts,
  metroCounts,
}: ProvidersFilterBarProps) {
  const [stickyRef, isStuck] = useIsStuck<HTMLDivElement>(STICKY_OFFSET_BELOW_SECTION_NAV);

  // Adapt the {value,label} option shape parent passes us back into
  // the {id,name} shape MetroAreaCombobox expects. MetroAreaCombobox
  // accepts a narrow `MetroLike` (id + name) precisely so this
  // adapter stays cast-free.
  const metrosForCombobox = useMemo(
    () => metroOptions.map((o) => ({ id: o.value, name: o.label })),
    [metroOptions],
  );

  return (
    <div
      ref={stickyRef}
      className={cn(
        'sticky top-[var(--h-topbar)] z-[99] min-[600px]:top-[var(--offset-nav-stack)] border-b border-border bg-ice transition-shadow',
        isStuck && 'shadow-sticky',
      )}
      role="region"
      aria-label="Providers filter bar"
    >
      <div className="mx-auto flex max-w-page flex-col gap-3 px-8 py-3 md:flex-row md:flex-wrap md:items-end md:gap-4">
        {/* 1. Metro Area — leftmost (gating selection). Width matched
            to OG so the three filters read as siblings. */}
        <div
          data-testid="providers-filter-slot-metro"
          className="md:w-filter-slot md:shrink-0"
        >
          <MetroAreaCombobox
            value={metroAreaId}
            onChange={onMetroAreaChange}
            metros={metrosForCombobox}
            defaultOptionLabel="All metro areas"
            counts={metroCounts}
          />
        </div>
        {/* 2. Occupational Group — single typeahead combobox shared
            with Detail / Map sidebar / Rankings for consistent
            type-to-narrow over the 22 occupational groups. */}
        <div
          data-testid="providers-filter-slot-og"
          className="md:w-filter-slot md:shrink-0"
        >
          <OccupationGroupCombobox
            value={occupationalGroupId}
            onChange={onOccupationalGroupChange}
            groups={OCCUPATIONAL_GROUPS}
            counts={ogCounts}
          />
        </div>
        {/* 3. Search — institution name. Width matched to Metro + OG
            so the three controls form an even row (2026-06-05). */}
        <div
          data-testid="providers-filter-slot-search"
          className="md:w-filter-slot md:shrink-0"
        >
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-4">
            Search
          </span>
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search providers…"
            ariaLabel="Search providers"
          />
        </div>
        {/* IMP-009 (Maria 2026-06-01 Zoom 9:23): ratio methodology
            pill — already on Home/Map/Rankings — surfaced here too so
            the affordance lives in a predictable spot across every
            data page. Sits between the filter controls and the
            right-aligned TableActions. */}
        <div className="md:self-end">
          <RatioMethodologyNote />
        </div>
        {/* Copy / Export / Print. Drops the previous `className=""`
            override so it picks up the default `md:ml-auto` and
            right-aligns inside the bar -- same position the Rankings
            and Detail pages use.
            getCsvData wired 2026-06-09 (Slack Luis + Maria) -- the
            page-level ProvidersTable serializes the currently-visible
            filtered rows into a CSV whose column set matches the
            <ProvidersTableView /> render (different columns surface
            when an OG filter is active vs. not). */}
        <TableActions
          getCsvData={getCsvData}
          filename="georgetown-cew-providers"
        />
      </div>
    </div>
  );
}
